/* istanbul ignore file */
/**
 * This file is a single webgl instance that loads a mask and recolors it. We can use thread pools to manage multiple instances.
 *
 * This is done by
 * - creating a canvas with a webgl renderer
 * - drawing a 2d plane (with a camera looking directly at it)
 * - loading the mask (image) as a texture on the 2d plane
 * - resizing the HTMLCanvasElement, renderer, and camera so that the 2d plane and canvas are the same exact dimensions
 * - converting the canvas to an image with `.toDataURL`
 *
 * @see https://threejs.org/manual/#en/fundamentals for more information and better visualization
 */
import { CanvasTexture, ImageBitmapLoader, Mesh, MeshBasicMaterial, OrthographicCamera, PlaneGeometry, Scene, Texture, WebGLRenderer } from 'three';

let renderer: WebGLRenderer;
let scene: Scene;
let camera: OrthographicCamera;
let plane: Mesh<PlaneGeometry, MeshBasicMaterial>; // 2d plane which will render the texture

/**
 * Create an offscreen canvas or HTML canvas depending on whats supported
 *
 * Prefer offscreen canvas for use with webworker
 */
function createRenderer(): void {
  let canvas: HTMLCanvasElement | OffscreenCanvas = document.createElement('canvas');

  if (canvas.transferControlToOffscreen !== undefined) {
    canvas = new OffscreenCanvas(1, 1);
  }
  try {
    renderer = new WebGLRenderer({
      canvas,
      preserveDrawingBuffer: true, // necessary for snapshots
      alpha: true, // transparent canvas bg
    });
  } catch (e) {
    console.error(e);
  }
}

/**
 * Init a renderer, camera, and scene
 */
function init(): void {
  createRenderer();

  const left = -1;
  const right = 1;
  const top = 1;
  const bottom = -1;
  const near = 0.1;
  const far = 50;
  try {
    camera = new OrthographicCamera(left, right, top, bottom, near, far);
    camera.position.set(0, 0, 1);
  } catch (e) {
    console.error(e);
  }

  scene = new Scene();
}

/**
 * Create a 2D plane with provided color, and texture used as an alpha map.
 *
 * An alpha map is just a grayscale image where black is transparent and white is opaque (and everything in between)
 */
function createObject(texture: Texture, color: string): void {
  const aspect = texture.image.width / texture.image.height;
  const size = 1;
  const geometry = new PlaneGeometry(aspect * size, size);

  const material = new MeshBasicMaterial({
    alphaMap: texture, // use as grayscale alpha map
    alphaTest: 0.5,
    color, // recolor the mask
  });

  // dispose old mesh
  // we could try and reuse a mesh but you can't edit the size of a mesh's geometry easily, you can only scale it
  if (plane) {
    scene.remove(plane);
    plane.geometry.dispose();
    plane.material.dispose();
    texture.dispose();
  }

  plane = new Mesh(geometry, material);
  scene.add(plane);
}

/**
 * Adjust the width and height so that
 *
 * 1. The canvas dimensions match those of the image, so when we take a snapshot of the canvas the dimensions will match
 * 2. Adjust the camera so that the 2d plane fits the entire canvas
 */
function resize(width: number, height: number): void {
  // set canvas element size as well as viewport size
  renderer.setSize(width, height, false);

  // adjust the camera, there's probably a simpler way to do this that doesn't involve zoom
  const aspect = width / height;
  camera.left = -aspect;
  camera.right = aspect;
  camera.zoom = 2;
  camera.updateProjectionMatrix();
  renderer.render(scene, camera);
}

/**
 * Create a snapshot of the renderer
 */
function createSnapshot(): Promise<string> {
  const canvas = renderer.domElement as HTMLCanvasElement | OffscreenCanvas;
  const blobToURL = (blob: Blob): string => URL.createObjectURL(blob);

  const promise =
    'convertToBlob' in canvas
      ? canvas.convertToBlob().then(blobToURL)
      : new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject();
            }
          });
        }).then(blobToURL);

  return promise;
}

/**
 * Recolor an image mask as a specified color. Also adds transparency to the image mask background.
 *
 *
 *
 * @param src URL or base64 string of a grayscale image mask
 * @returns recolored image mask
 */
export function recolorMask(src: string, color: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const loader = new ImageBitmapLoader();
    loader.setOptions({ imageOrientation: 'flipY' });
    loader.load(
      src,
      (bitmap: ImageBitmap) => {
        const texture = new CanvasTexture(bitmap);
        createObject(texture, color);
        resize(texture.image.width, texture.image.height);
        resolve(createSnapshot());
      },
      () => {},
      reject,
    );
  });
}

if (typeof window !== 'undefined') {
  init();
}
